import { useCookies } from '/src/composables/useCookies.js';

/**
 * CookiesAdapter - Adapts useCookies to persist plugin contract
 *
 * This adapter translates the persist plugin's contract (get, set, remove, clear)
 * to the full cookies API provided by useCookies composable.
 *
 * Note: Cookies have limitations:
 * - 4KB size limit per cookie
 * - Domain/path restrictions
 * - Not recommended for large data storage
 *
 * @param {Object} options - Cookie options
 * @param {number} options.maxAge - Max age in seconds (default: 365 days)
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain
 * @param {boolean} options.secure - Secure flag
 * @param {string} options.sameSite - SameSite attribute (default: 'Lax')
 * @returns {Object} Persist plugin storage adapter
 */
export function createCookiesAdapter (options = {}) {
	const { getItem, setItem, removeItem, clear } = useCookies();

	// Default: cookies expire in 1 year
	const defaultMaxAge = options.maxAge ?? 365 * 24 * 60 * 60;
	const cookieOptions = {
		path: options.path || '/',
		domain: options.domain,
		secure: options.secure,
		sameSite: options.sameSite || 'Lax',
	};

	return {
		/**
         * Get a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to retrieve
         * @returns {Promise<{value: string}>} Object with value property
         * @throws {Error} If key not found
         */
		async get ({ key }) {
			const value = await getItem(key);

			if (value === null) {
				throw new Error(`Cookie "${key}" not found`);
			}

			return { value };
		},

		/**
         * Set a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to set
         * @param {string} params.value - The value to store (already serialized)
         * @returns {Promise<void>}
         */
		async set ({ key, value }) {
			await setItem(key, value, {
				...cookieOptions,
				maxAge: defaultMaxAge,
			});
		},

		/**
         * Remove a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to remove
         * @returns {Promise<void>}
         */
		async remove ({ key }) {
			await removeItem(key, cookieOptions);
		},

		/**
         * Clear all values (persist plugin contract)
         * Note: Only clears cookies accessible to current path/domain
         * @returns {Promise<void>}
         */
		async clear () {
			await clear();
		},
	};
}
